// dwsql — datawiki's flavour of parameterized SQL.
//
// Two features, no template language:
//
//   $name / $name::type   A bound parameter. Compiles to a placeholder plus an
//                         ordered values array, so values are never spliced into
//                         SQL text. Postgres: positional `$1..$N`, and the ::type
//                         cast stays in the output. SQLite: named `$name`, and
//                         the ::type cast is dropped (see Dialect). Either way the
//                         ::type declares the parameter's type (auto-UI, the
//                         conflict check below, and SQLite value coercion).
//
//   @ref                  An include. Pure text expansion: `@ref` is replaced
//                         with the (recursively compiled) SQL of the resolved
//                         file. The author writes the surrounding structure —
//                         e.g. `with u as (@active-users)` or
//                         `select * from (@active-users) t`. Less magic than
//                         Metabase: no auto-CTEs, no alias inference, no
//                         per-include arguments.
//
// Parameters share ONE flat namespace across the whole include tree: the same
// `$name` anywhere binds to one value. If a name is declared with conflicting
// ::types across files (or within one), compilation throws.
//
// The module is pure — no filesystem, no database. Includes are resolved
// through a caller-supplied `resolve(ref, fromPath)` callback that returns the
// SQL plus a canonical `path`. The search path (where SQL lives, walk-up rules)
// is the resolver's concern; the lib uses `path` only as the identity for cycle
// detection — the same `@ref` can resolve to different files from different
// locations, so cycles key on path, not ref.

import { tokenize, type Token } from "./lex.js";

export class DwsqlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DwsqlError";
  }
}

/** A type name as written in a `::cast`, e.g. "date", "numeric(10,2)", "int[]". */
type TypeName = string;

/**
 * Target SQL engine. Differs only in parameter emission:
 *   postgres — positional `$1..$N`; the `::type` cast stays in the text.
 *   duckdb   — same as postgres (DuckDB has `::` casts and `$1` params), so it
 *              reuses the postgres emission path.
 *   sqlite   — named `$name` (SQLite supports it, and reuse binds once); the
 *              `::type` cast is dropped (SQLite has no `::`), leaving the type as
 *              metadata only. `@ref` includes expand identically either way.
 */
export type Dialect = "postgres" | "duckdb" | "sqlite";

export interface ParamInfo {
  name: string;
  /** Declared type from a `::cast` or header declaration, or null if untyped. */
  type: TypeName | null;
  /** Positional placeholder assigned in the compiled SQL (1-based). */
  position: number;
}

export interface ParamDecl {
  name: string;
  type: TypeName | null;
  /** A default value declared in a `-- $name::type = default` header. */
  default?: unknown;
  hasDefault: boolean;
}

export interface Analysis {
  /** Parameters referenced or declared in this SQL, first-seen order. */
  params: ParamDecl[];
  /** Include references in source order, de-duplicated. */
  includes: string[];
}

export interface Resolved {
  sql: string;
  /** Canonical identity of the resolved file, used for cycle detection. */
  path: string;
}

export type Resolver = (
  ref: string,
  fromPath: string | null,
) => Resolved | null | undefined;

export interface CompileOptions {
  /** Values to bind, keyed by parameter name. */
  values?: Record<string, unknown>;
  /** Resolves an include ref against `fromPath`. Required if the SQL has includes. */
  resolve?: Resolver;
  /** Path of the root SQL/doc — the search base, and the root of cycle detection. */
  path?: string | null;
  /** Maximum include nesting depth before giving up. Default 64. */
  maxDepth?: number;
  /** Target engine for parameter emission. Default "postgres". */
  dialect?: Dialect;
}

export interface CompileResult {
  /**
   * Compiled SQL. Postgres: `$1..$N` placeholders. SQLite: named `$name`
   * placeholders with `::type` casts stripped.
   */
  text: string;
  /**
   * Values in `params` order; `values[k]` corresponds to `params[k]`. For
   * Postgres that's placeholder order ($(k+1)); for SQLite, bind by name using
   * `params[k].name`.
   */
  values: unknown[];
  /** Parameters actually bound, in order. */
  params: ParamInfo[];
  /**
   * Directives read from the *flattened* query (after every `@ref` is inlined),
   * not the raw request text. `source` is the single `-- @source:` id found
   * anywhere in the tree (conflicting ids throw during compile); `maxAge` is a
   * `-- @maxAge:` directive, honored only when it sits in the leading comment
   * block. See Pragmas.md.
   */
  pragmas: Pragmas;
}

export interface Pragmas {
  /** The `@source` id declared anywhere in the flattened query, or null. */
  source: string | null;
  /** A leading (top-of-query) `@maxAge` value verbatim (e.g. "10m"), or null. */
  maxAge: string | null;
}

// --- type names ------------------------------------------------------------

// A simplified type matcher: name, optional schema-qualifier, optional
// precision, optional array suffix. Good enough for casts authors actually
// write (date, timestamptz, numeric(10,2), int[], public.my_type). Multi-word
// types like `timestamp with time zone` only capture their first word for
// detection purposes — the SQL itself is never rewritten, so it still runs.
const TYPE = String.raw`[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)?(?:\([^)]*\))?(?:\s*\[\s*\])?`;
const CAST_RE = new RegExp(String.raw`^\s*::\s*(${TYPE})`);
const DECL_RE = new RegExp(
  String.raw`^([A-Za-z_]\w*)\s*(?:::\s*(${TYPE}))?\s*(?:=\s*(.*\S))?$`,
);

const normType = (t: TypeName): string => t.toLowerCase().replace(/\s+/g, "");

// --- header declarations & literals ---------------------------------------

interface ParsedDecl {
  name: string;
  type: TypeName | null;
  default?: unknown;
  hasDefault: boolean;
}

/**
 * Parse a declaration comment, or return null if it isn't one. Accepts:
 *   -- $name::type = default
 *   -- $name::type
 *   -- $name = default
 *   -- @param $name::type = default   (the `@param` prefix is optional)
 */
function parseDecl(commentText: string): ParsedDecl | null {
  let body = commentText.replace(/^--/, "").trim();
  // A declaration is marked by the `$` sigil or the `@param` keyword; anything
  // else is just an ordinary comment.
  const hadParam = /^@param\s+/i.test(body);
  if (hadParam) body = body.replace(/^@param\s+/i, "");
  if (!hadParam && !body.startsWith("$")) return null;
  body = body.replace(/^\$/, "");
  const m = DECL_RE.exec(body);
  if (!m) return null;
  const [, name, type, def] = m;
  const decl: ParsedDecl = {
    name,
    type: type ?? null,
    hasDefault: def !== undefined,
  };
  if (def !== undefined) decl.default = parseLiteral(def);
  return decl;
}

/** Convert a SQL literal (as written in a header default) to a JS value. */
function parseLiteral(text: string): unknown {
  const t = text.trim();
  const str = /^'([\s\S]*)'$/.exec(t);
  if (str) return str[1].replace(/''/g, "'");
  if (/^-?\d+$/.test(t)) return Number(t);
  if (/^-?(?:\d+\.\d*|\.\d+|\d+)(?:[eE][+-]?\d+)?$/.test(t)) return Number(t);
  if (/^true$/i.test(t)) return true;
  if (/^false$/i.test(t)) return false;
  if (/^null$/i.test(t)) return null;
  return t; // bare word: keep verbatim
}

// --- declaration collection (with conflict detection) ----------------------

type DeclMap = Map<string, ParamDecl>;

function mergeDecl(
  decls: DeclMap,
  name: string,
  type: TypeName | null,
  def?: { value: unknown },
): void {
  const existing = decls.get(name);
  if (!existing) {
    decls.set(name, {
      name,
      type,
      hasDefault: def !== undefined,
      ...(def !== undefined ? { default: def.value } : {}),
    });
    return;
  }
  if (type && existing.type && normType(type) !== normType(existing.type)) {
    throw new DwsqlError(
      `parameter $${name} has conflicting types: ${existing.type} and ${type}`,
    );
  }
  if (type && !existing.type) existing.type = type;
  if (def !== undefined && !existing.hasDefault) {
    existing.hasDefault = true;
    existing.default = def.value;
  }
}

/**
 * Record every parameter declaration in `tokens` into `decls`, from both
 * header comments and inline `$name::type` usages. Throws on a type conflict.
 */
function collectDecls(tokens: Token[], decls: DeclMap): void {
  for (let k = 0; k < tokens.length; k++) {
    const tk = tokens[k];
    if (tk.kind === "lineComment") {
      const d = parseDecl(tk.text);
      if (d) {
        mergeDecl(
          decls,
          d.name,
          d.type,
          d.hasDefault ? { value: d.default } : undefined,
        );
      }
    } else if (tk.kind === "param") {
      // An inline `$name::type` declares the type. The cast lives at the start
      // of the next code token.
      const next = tokens[k + 1];
      let type: TypeName | null = null;
      if (next && next.kind === "code") {
        const m = CAST_RE.exec(next.text);
        if (m) type = m[1].trim();
      }
      mergeDecl(decls, tk.name!, type);
    }
  }
}

// --- analyze (local, no I/O) -----------------------------------------------

/** Statically analyze one SQL string: its parameters and include refs. No
 * recursion — `params` covers only this file. Use `compile` to flatten an
 * include tree. */
export function analyze(sql: string): Analysis {
  const tokens = tokenize(sql);
  const decls: DeclMap = new Map();
  const order: string[] = [];

  // Param order = first appearance among real usages, then any decl-only names.
  for (const tk of tokens) {
    if (tk.kind === "param" && !order.includes(tk.name!)) order.push(tk.name!);
  }
  collectDecls(tokens, decls);
  for (const name of decls.keys()) if (!order.includes(name)) order.push(name);

  const includes: string[] = [];
  for (const tk of tokens) {
    if (tk.kind === "include" && !includes.includes(tk.ref!)) {
      includes.push(tk.ref!);
    }
  }

  return {
    params: order.map(
      (name) => decls.get(name) ?? { name, type: null, hasDefault: false },
    ),
    includes,
  };
}

// --- compile (expand includes, bind values) --------------------------------

/**
 * Recursively expand `sql` into a flat token list, resolving every `@ref`
 * include through `opts.resolve`. Declaration comments are dropped from the
 * output (they're metadata); their types/defaults are merged into `decls`.
 */
function expand(
  sql: string,
  opts: CompileOptions,
  decls: DeclMap,
  stack: (string | null)[],
): Token[] {
  if (stack.length > (opts.maxDepth ?? 64)) {
    throw new DwsqlError(`include nesting too deep (> ${opts.maxDepth ?? 64})`);
  }

  const tokens = tokenize(sql);
  collectDecls(tokens, decls);

  const fromPath = stack[stack.length - 1];
  const out: Token[] = [];
  for (const tk of tokens) {
    if (tk.kind === "include") {
      const childRef = tk.ref!;
      if (!opts.resolve) {
        throw new DwsqlError(
          `cannot resolve include @${childRef}: no resolver provided`,
        );
      }
      const resolved = opts.resolve(childRef, fromPath);
      if (resolved == null) {
        throw new DwsqlError(`unknown include: @${childRef}`);
      }
      if (stack.includes(resolved.path)) {
        const chain = [...stack, resolved.path]
          .map((p) => p ?? "<root>")
          .join(" → ");
        throw new DwsqlError(`include cycle: ${chain}`);
      }
      out.push(...expand(resolved.sql, opts, decls, [...stack, resolved.path]));
    } else if (tk.kind === "lineComment" && parseDecl(tk.text)) {
      // drop declaration comments from the emitted SQL
    } else {
      out.push(tk);
    }
  }
  return out;
}

const SOURCE_RE = /^\s*--\s*@source:\s*([\w-]+)/i;
const MAXAGE_RE = /^\s*--\s*@maxAge:\s*(\S+)/i;

/**
 * Read `@source` / `@maxAge` directives off a *flattened* token stream (every
 * `@ref` already inlined). Two deliberately different rules:
 *
 *  - `@source` is gathered from a comment *anywhere* in the tree, because it
 *    picks the single database the whole statement runs against. A query that
 *    inlines two fragments declaring different sources cannot run against both,
 *    so a conflict is a hard error rather than a silent first-wins pick.
 *  - `@maxAge` is honored *only* in the leading comment block (before any SQL).
 *    A reusable fragment buried mid-query shouldn't leak its cache window onto
 *    every query that includes it; the freshness knob belongs to the query's
 *    own header.
 */
function extractPragmas(tokens: Token[]): Pragmas {
  const sources = new Set<string>();
  let maxAge: string | null = null;
  let atTop = true;
  for (const tk of tokens) {
    if (tk.kind === "lineComment") {
      const sm = SOURCE_RE.exec(tk.text);
      if (sm) sources.add(sm[1].toLowerCase());
      if (atTop && maxAge === null) {
        const mm = MAXAGE_RE.exec(tk.text);
        if (mm) maxAge = mm[1];
      }
    } else if (tk.kind === "blockComment" || tk.text.trim() === "") {
      // Comments and whitespace-only spans keep us in the leading block.
    } else {
      atTop = false; // first real SQL closes the header
    }
  }
  if (sources.size > 1) {
    throw new DwsqlError(
      `conflicting @source: ${[...sources].sort().join(", ")} — a query runs against a single source`,
    );
  }
  return { source: sources.size ? [...sources][0] : null, maxAge };
}

export function compile(sql: string, opts: CompileOptions = {}): CompileResult {
  const decls: DeclMap = new Map();
  const tokens = expand(sql, opts, decls, [opts.path ?? null]);
  // Pragmas come from the flattened stream, so a `@ref` body's `@source` /
  // `@maxAge` are read from the file — conflicting sources throw here, before
  // any value binding, so the failure is structural not param-dependent.
  const pragmas = extractPragmas(tokens);

  // Emit the flattened stream, assigning placeholders. A name reused anywhere
  // reuses its placeholder and binds a single value (positional reuse on
  // Postgres, name reuse on SQLite).
  const dialect = opts.dialect ?? "postgres";
  const order: string[] = [];
  const position = new Map<string, number>();
  let text = "";
  // SQLite has no `::` cast, so after a named param we strip a leading `::type`
  // from the following code token. The type survives in `params` (metadata).
  let stripCast = false;
  for (const tk of tokens) {
    if (tk.kind === "param") {
      let pos = position.get(tk.name!);
      if (pos === undefined) {
        order.push(tk.name!);
        pos = order.length;
        position.set(tk.name!, pos);
      }
      if (dialect === "sqlite") {
        text += "$" + tk.name;
        stripCast = true;
      } else {
        text += "$" + pos;
      }
    } else {
      text += stripCast ? tk.text.replace(CAST_RE, "") : tk.text;
      stripCast = false;
    }
  }

  const provided = opts.values ?? {};
  const values = order.map((name) => {
    if (Object.prototype.hasOwnProperty.call(provided, name)) {
      return provided[name];
    }
    const decl = decls.get(name);
    if (decl?.hasDefault) return decl.default;
    throw new DwsqlError(`no value provided for parameter $${name}`);
  });

  const params: ParamInfo[] = order.map((name, idx) => ({
    name,
    type: decls.get(name)?.type ?? null,
    position: idx + 1,
  }));

  return { text, values, params, pragmas };
}
